import { Button, ManageableItemsList, Progress } from '@payaca/ambrosia';
import { FC } from 'react';

import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { MaterialsListMaterials } from '@/api/queries/project/useGetProjectMaterialsListMaterials';
import { humanizeCurrency } from '@/helpers/currency';

import {
  getAccountRegionFromCurrencyCodeLegacy,
  getRegionalTextString,
} from '@payaca/helpers/internationalHelper';
import { AccountRegions } from '@payaca/types/accountTypes';
import { RegionalStrings } from '@payaca/types/internationalTypes';
type MaterialsListMaterial = MaterialsListMaterials['items'][0];

export const SummaryPanel: FC<{
  summaryItems: MaterialsListMaterials;
  onPageChange: (page: number) => void;
  onMaterialView: (materialId: string) => void;
}> = (props) => {
  const { summaryItems, onPageChange, onMaterialView } = props;

  const { data: regionPreferences } = useGetMyRegionPreferences();

  const region = regionPreferences
    ? getAccountRegionFromCurrencyCodeLegacy(regionPreferences?.currency.code)
    : AccountRegions.UK;

  return (
    <div className="@container">
      <ManageableItemsList.Table<MaterialsListMaterial>
        items={summaryItems.items}
        uniqueKey="id"
      >
        <ManageableItemsList.Table.Column<MaterialsListMaterial, 'id'>
          header="Material"
          field="id"
          render={(id, row) => (
            <Button
              variant="link-inline"
              onClick={() => onMaterialView(row.material.id)}
            >
              {row.material.name}
            </Button>
          )}
        />
        <ManageableItemsList.Table.Column<MaterialsListMaterial, 'id'>
          header="Order Progress"
          field="id"
          render={(id, row) => {
            return (
              <div className="flex items-center gap-4">
                <span className="grow">
                  {row.quantityOrdered}/{row.quantity}
                </span>

                <Progress
                  bars={[
                    {
                      progress: row.quantityOrdered,
                      total: row.quantity,
                      colour: 'purple',
                    },
                  ]}
                />
              </div>
            );
          }}
        />
        <ManageableItemsList.Table.Column<MaterialsListMaterial, 'id'>
          className="text-right"
          header={`Forecast Cost (ex ${getRegionalTextString(
            region,
            RegionalStrings.VALUE_ADDED_TAX
          )})`}
          field="id"
          render={(id, row) => {
            if (row.material.suppliedBy.length) {
              const price = row.material.suppliedBy[0].price.unitPriceExcTax;

              return humanizeCurrency({
                value: Math.round(price.value * row.quantity),
                currency: price.currency,
              });
            }

            return;
          }}
        />

        <ManageableItemsList.Table.Column<MaterialsListMaterial, 'id'>
          className="text-right"
          header={`Purchase Cost (ex ${getRegionalTextString(
            region,
            RegionalStrings.VALUE_ADDED_TAX
          )})`}
          field="id"
          render={(id, row) => {
            if (row.materialPurchaseRecords.length) {
              const purchased = row.materialPurchaseRecords.reduce(
                (acc, record) => {
                  return acc + (record.price.totalPriceExcTax?.value || 0);
                },
                0
              );

              const currency =
                row.materialPurchaseRecords[0].price.totalPriceExcTax?.currency;
              return humanizeCurrency({ value: purchased, currency });
            } else {
              return;
            }
          }}
        />
      </ManageableItemsList.Table>
      <ManageableItemsList.PaginationBar
        type="custom"
        totalItems={summaryItems.totalCount}
        pageSize={summaryItems.limit}
        currentPage={summaryItems.offset / summaryItems.limit + 1}
        onPageChange={onPageChange}
      />
    </div>
  );
};
